<template>
  <v-container fluid>
    <v-row align="baseline">
      <v-col>
        <v-text-field
          label="Min Pax"
          v-model="minPax"
          type="number"
          :outlined="outlinedInputs"
          hide-details
          min="1"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          label="Max Pax"
          v-model="maxPax"
          type="number"
          :outlined="outlinedInputs"
          hide-details
          min="1"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          label="Step"
          v-model="step"
          type="number"
          :outlined="outlinedInputs"
          hide-details
          min="1"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          label="Adjust %"
          v-model.number="percentAdjustment"
          type="number"
          :outlined="outlinedInputs"
          hide-details
        />
      </v-col>
      <div>
        <list-visible-columns-and-filter
          :visible-columns="visibleColumns"
          :selectable-headers="selectableHeaders"
          @change="onVisibleColumnsChange"
          hide-search
        ></list-visible-columns-and-filter>
      </div>
    </v-row>
    <v-divider class="mb-3" />
    <v-row v-for="acc in accommodations" :key="acc.name">
      <v-col class="py-0">
        <h4>{{ acc.name }}</h4>
        <v-row>
          <v-col>
            <v-text-field
              label="Adults FOC"
              v-model="settingsByRoomType[acc.occupancy].adultFoc"
              type="number"
              :outlined="outlinedInputs"
              hide-details
              dense
              @change="updateAdjustments('foc', $event, acc.occupancy)"
            />
          </v-col>
          <v-col>
            <v-text-field
              label="Children FOC"
              v-model="settingsByRoomType[acc.occupancy].childFoc"
              type="number"
              :outlined="outlinedInputs"
              hide-details
              dense
              @change="updateAdjustments('childFoc', $event, acc.occupancy)"
            />
          </v-col>
          <v-col>
            <v-text-field
              label="Infants FOC"
              v-model="settingsByRoomType[acc.occupancy].infantFoc"
              type="number"
              :outlined="outlinedInputs"
              hide-details
              dense
              @change="updateAdjustments('infantFoc', $event, acc.occupancy)"
            />
          </v-col>
          <v-col>
            <v-text-field
              label="Adults $"
              v-model="settingsByRoomType[acc.occupancy].adultAdjustment"
              type="number"
              :outlined="outlinedInputs"
              hide-details
              dense
              @change="updateAdjustments('adjustment', $event, acc.occupancy)"
            />
          </v-col>
          <v-col>
            <v-text-field
              label="Children $"
              v-model="settingsByRoomType[acc.occupancy].childAdjustment"
              type="number"
              :outlined="outlinedInputs"
              hide-details
              dense
              @change="
                updateAdjustments('childAdjustment', $event, acc.occupancy)
              "
            />
          </v-col>
          <v-col>
            <v-text-field
              label="Infants $"
              v-model="settingsByRoomType[acc.occupancy].infantAdjustment"
              type="number"
              :outlined="outlinedInputs"
              hide-details
              dense
              @change="
                updateAdjustments('infantAdjustment', $event, acc.occupancy)
              "
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-for="item in items" :key="item.uid">
      <v-col>
        <pax-pricing
          :paxFrom="item.paxFrom"
          :paxTo="item.paxTo"
          :items="servicesPricing"
          :accommodations="accommodations"
          @change="onPaxPricingChange"
          :adjustment="item.adjustment"
          @create-itinerary="onShowCreateItinerary"
          :show-create-new-iti="showCreateNewIti"
          :for-itinerary="forItinerary"
          is-combined
          :percent-adjustment="percentAdjustment"
          :charges-headers="filteredHeaders"
          :suppliers-comps="suppliersComps"
          :mandatory-charges="mandatoryCharges"
        />
      </v-col>
    </v-row>
    <div v-if="debug">
      <code>items: {{ adjustments }}</code>
    </div>
    <v-row justify="center">
      <confirmation-dialog @onConfirm="onDelete">
        <v-btn color="warning" text class="mx-3">Delete</v-btn>
        <template slot="message">Are you sure you want to delete? </template>
      </confirmation-dialog>
      <v-btn text color="primary" @click="onSave">Save Changes</v-btn></v-row
    >
    <template-settings-dialog
      :show="showCreateItineraryFromTemplate"
      :selected-template="templateData"
      @createItinerary="onCreateItinerary"
      @onCancel="showCreateItineraryFromTemplate = false"
      :initialStartDate="pricingDate"
      create-itinerary
      hide-start-date
      include-settings
    ></template-settings-dialog>
  </v-container>
</template>

<script>
import { ITINERARY_RECALCULATE_MUTATION } from "../../../../../constants/graphql";
import { removeGQLTypename } from "../../../../../helpers";
import PaxPricing from "./PaxPricingCombined";
import { accommodationBucket } from "./helpers";
import TemplateSettingsDialog from "../../TemplateSettingsDialog";
import gql from "graphql-tag";
import ListVisibleColumnsAndFilter from "../../../shared/ListVisibleColumnsAndFilter.vue";
import ConfirmationDialog from "../../../shared/ConfirmationDialog.vue";
const moment = require("moment");
const randomize = require("randomatic");
const CREATE_ITINERARY = gql`
  mutation incrementCombinedCreateItinerary($input: ItineraryInput!) {
    createItinerary(itineraryInput: $input) {
      id
      reference
    }
  }
`;
export default {
  name: "increment-combined",

  props: {
    increment: {
      type: Object,
      default: () => null,
    },
    templateItems: {
      type: Array,
      default: () => [],
    },
    templateId: {
      type: String,
      default: () => "",
    },
    department: {
      type: String,
      default: () => "FIT",
    },
    destination: {
      type: String,
      default: () => "",
    },
    forItinerary: {
      type: Boolean,
      default: () => false,
    },
    forAdults: {
      type: Boolean,
      default: () => false,
    },
    forChildren: {
      type: Boolean,
      default: () => false,
    },
    forInfants: {
      type: Boolean,
      default: () => false,
    },
    agentId: {
      type: String,
      default: () => null,
    },
    suppliersComps: {
      type: Object,
      default: () => null,
    },
    showCreateNewIti: {
      type: Boolean,
      default: () => false,
    },
    mandatoryCharges: {
      type: Array,
      default: () => [],
    },
  },

  components: {
    PaxPricing,
    TemplateSettingsDialog,
    ListVisibleColumnsAndFilter,
    ConfirmationDialog,
  },

  data() {
    return {
      debug: false,
      step: this.increment ? this.increment.step : 2,
      minPax: this.increment ? this.increment.minPax : 1,
      maxPax: this.increment ? this.increment.maxPax : 10,
      pricingDate: this.increment
        ? this.increment.date
        : moment().format("YYYY-MM-DD"),
      itinerary: null,
      itineraryForChildren: null,
      itineraryForInfants: null,
      adjustments:
        this.increment && this.increment.adjustments
          ? this.increment.adjustments.edges.map(removeGQLTypename)
          : [],
      templateData: {
        id: this.templateId,
        items: this.templateItems,
        destinationId: this.destination,
        department: this.department,
      },
      showCreateItineraryFromTemplate: false,
      itiCharges: [],
      duration: 10,
      adultsFOC: null,
      childrenFOC: null,
      infantsFOC: null,
      adultsAdjustment: null,
      childrenAdjustment: null,
      infantsAdjustment: null,
      percentAdjustment: null,
      visibleColumns: this.$store.getters.localSettings("incrementsColumns", [
        "name",
        "pp",
        "foc",
        "adjustment",
        "percent",
        "total",
      ]),
      headers: [
        {
          text: "Charges",
          value: "name",
          name: "name",
        },
        {
          text: "Adults PP",
          value: "pp",
          name: "pp",
        },
        {
          text: "Comp Value",
          value: "compValue",
          name: "compValue",
        },
        {
          text: "Adults FOC",
          value: "foc",
          name: "foc",
        },
        {
          text: "Adults Adjust",
          value: "adjustment",
          name: "adjustment",
        },
        {
          text: "Adults %",
          value: "percent",
          name: "percent",
        },
        {
          text: "Adults Net",
          value: "total",
          name: "total",
        },
        {
          text: "Children PP",
          value: "childrenPP",
          name: "childrenPP",
        },
        {
          text: "Children FOC",
          value: "childFoc",
          name: "childFoc",
        },
        {
          text: "Children Adjust",
          value: "childAdjustment",
          name: "childAdjustment",
        },
        {
          text: "Children %",
          value: "childPercent",
          name: "childPercent",
        },
        {
          text: "Children Net",
          value: "childTotal",
          name: "childTotal",
        },
        {
          text: "Infants PP",
          value: "infantsPP",
          name: "infantsPP",
        },
        {
          text: "Infants FOC",
          value: "infantFoc",
          name: "infantFoc",
        },
        {
          text: "Infants Adjust",
          value: "infantAdjustment",
          name: "infantAdjustment",
        },
        {
          text: "Infants %",
          value: "infantPercent",
          name: "infantPercent",
        },
        {
          text: "Infants Net",
          value: "infantTotal",
          name: "infantTotal",
        },
      ],
      settingsByRoomType: {
        1: {
          adultFoc: 0,
          childFoc: 0,
          infantFoc: 0,
          adultAdjustment: 0,
          childAdjustment: 0,
          infantAdjustment: 0,
          percentAdjustment: 0,
        },
        2: {
          adultFoc: 0,
          childFoc: 0,
          infantFoc: 0,
          adultAdjustment: 0,
          childAdjustment: 0,
          infantAdjustment: 0,
          percentAdjustment: 0,
        },
        3: {
          adultFoc: 0,
          childFoc: 0,
          infantFoc: 0,
          adultAdjustment: 0,
          childAdjustment: 0,
          infantAdjustment: 0,
          percentAdjustment: 0,
        },
        4: {
          adultFoc: 0,
          childFoc: 0,
          infantFoc: 0,
          adultAdjustment: 0,
          childAdjustment: 0,
          infantAdjustment: 0,
          percentAdjustment: 0,
        },
        5: {
          adultFoc: 0,
          childFoc: 0,
          infantFoc: 0,
          adultAdjustment: 0,
          childAdjustment: 0,
          infantAdjustment: 0,
          percentAdjustment: 0,
        },
        100: {
          adultFoc: 0,
          childFoc: 0,
          infantFoc: 0,
          adultAdjustment: 0,
          childAdjustment: 0,
          infantAdjustment: 0,
          percentAdjustment: 0,
        },
        200: {
          adultFoc: 0,
          childFoc: 0,
          infantFoc: 0,
          adultAdjustment: 0,
          childAdjustment: 0,
          infantAdjustment: 0,
          percentAdjustment: 0,
        },
      },
    };
  },

  computed: {
    selectableHeaders() {
      return this.headers.filter((h) => h.name != "name");
    },
    filteredHeaders() {
      return this.headers.filter(
        (h) => this.visibleColumns.indexOf(h.name) > -1
      );
    },
    paxType() {
      let paxType = "adults";
      if (this.forChildren) {
        paxType = "children";
      } else if (this.forInfants) {
        paxType = "infants";
      }
      return paxType;
    },
    itineraryItemsWithSupplierComps() {
      if (this.itinerary) {
        if (this.suppliersComps) {
          const withComps = [...this.itinerary.items.edges];
          return withComps;
        } else {
          return this.itinerary.items.edges;
        }
      } else {
        return [];
      }
    },
    servicesPricing() {
      return this.itinerary &&
        this.itineraryForChildren &&
        this.itineraryForInfants &&
        this.itinerary.items
        ? this.itinerary.items.edges.map((item) => {
            return {
              id: item.node.id,
              supplierId: item.node.supplier.id,
              serviceId: item.node.service.id,
              serviceName: item.node.serviceName,
              supplierName: item.node.supplierName,
              isIncluded: item.node.isIncluded,
              isOptional: item.node.isOptional,
              rateType: item.node.rateTypeCode,
              price: this.getPriceByPaxType("adults", item.node),
              childrenPrice: this.getPriceByPaxType("children", item.node),
              infantsPrice: this.getPriceByPaxType("infants", item.node),
              serviceTypeName: item.node.serviceTypeName,
              stdOccupancy: Number(item.node.service.stdOccupancy),
              typeName: item.node.service && item.node.service.typeName,
              extras: item.node.extras.map((extra) => {
                const price = this.getPriceForExtra(extra);
                return {
                  id: extra.id,
                  name: extra.name,
                  price: extra.isChildOnly || extra.isInfantOnly ? 0 : price,
                  childPrice: extra.isChildOnly ? price : 0,
                  infantPrice: extra.isInfantOnly ? price : 0,
                  serviceTypeName: extra.serviceTypeName,
                  serviceName: item.node.serviceName,
                  supplierName: item.node.supplierName,
                  isChildOnly: extra.isChildOnly,
                  isInfantOnly: extra.isInfantOnly,
                  isIncluded: item.node.isIncluded,
                  isOptional: item.node.isOptional,
                  //...extra,
                };
              }),
            };
          })
        : [];
    },
    accommodations() {
      const roomTypes = {
        1: "Single",
        2: "Double/Twin",
        3: "Triple",
        4: "Quad",
        100: "Multi Bed",
        200: "Guide",
      };
      const byRoomType = this.servicesPricing
        .filter(
          (item) => item.serviceTypeName == "ACCOMMODATION" && item.isIncluded
        )
        .reduce((h, item) => {
          const key = accommodationBucket(item.stdOccupancy, item.typeName);
          if (!h[key]) h[key] = [];
          h[key].push(item);
          return h;
        }, {});
      return Object.keys(byRoomType).reduce((rooms, occupancy) => {
        rooms.push({
          name: roomTypes[occupancy],
          occupancy,
          pp: byRoomType[occupancy].reduce(
            (sum, item) => sum + Number(item.price),
            0
          ),
          childrenPP: byRoomType[occupancy].reduce(
            (sum, item) => sum + Number(item.childrenPrice),
            0
          ),
          infantsPP: byRoomType[occupancy].reduce(
            (sum, item) => sum + Number(item.infantsPrice),
            0
          ),
        });

        return rooms;
      }, []);
    },
    childrenItiInput() {
      const headerPax = this.increment ? this.increment.maxPax.toString() : "2";
      const itiInput = { ...this.itineraryInput };
      itiInput.adults = "0";
      itiInput.children = headerPax;
      itiInput.infants = "0";
      itiInput.items = itiInput.items.map((item) => {
        const pax = Number(item.adults);
        return { ...item, adults: 0, children: pax, infants: 0 };
      });
      return itiInput;
    },
    infantsItiInput() {
      const headerPax = this.increment ? this.increment.maxPax.toString() : "2";
      const itiInput = { ...this.itineraryInput };
      itiInput.adults = "0";
      itiInput.children = "0";
      itiInput.infants = headerPax;
      itiInput.items = itiInput.items.map((item) => {
        const pax = Number(item.adults);
        return { ...item, adults: 0, children: 0, infants: pax };
      });
      return itiInput;
    },
    itineraryInput() {
      const input = {
        adults: this.increment ? this.increment.maxPax.toString() : "2",
        children: "0",
        infants: "0",
        department: this.department,
        destinationId: this.destination,
        agentId: this.agentId ? this.agentId : this.$store.getters.agentId,
        startDate: this.pricingDate,
        endDate: moment(this.pricingDate)
          .add(this.duration, "days")
          .toISOString(),
        participants: [{ name: "TBA", surname: "TBA" }],
        items: this.templateItems.map((templateItem) => {
          const noOfPax = parseInt(templateItem.stdOccupancy);
          const isAccommodation = templateItem.serviceType == "ACCOMMODATION";
          const paxAdults = templateItem.pax ? templateItem.pax.adults : 0;
          const adults = isAccommodation
            ? noOfPax
            : noOfPax > 0 && paxAdults > noOfPax
            ? noOfPax
            : paxAdults > 0
            ? paxAdults
            : noOfPax || 1;
          const children = isAccommodation
            ? 0
            : templateItem.pax
            ? templateItem.pax.children
            : 0;
          const infants = isAccommodation
            ? 0
            : templateItem.pax
            ? templateItem.pax.infants
            : 0;
          const qty = 1;
          const itemDay = Number(templateItem.day) - 1;
          let itemStartDate = moment(this.pricingDate)
            .startOf("day")
            .add(itemDay, "days");
          const itemDuration =
            templateItem.serviceType == "ACCOMMODATION"
              ? Number(templateItem.duration)
              : Number(templateItem.duration) - 1;
          let itemEndDate = moment(itemStartDate)
            .startOf("day")
            .add(itemDuration, "days");
          //console.log(templateItem.hasOwnProperty("startDate"), templateItem);
          if (
            templateItem.hasOwnProperty("startDate") &&
            templateItem.hasOwnProperty("endDate")
          ) {
            itemStartDate = templateItem.startDate;
            itemEndDate = templateItem.endDate;
          }
          return {
            id: templateItem.uid,
            orderNo: templateItem.orderNo,
            serviceId: templateItem.serviceId,
            startDate: itemStartDate,
            endDate: itemEndDate,
            adults,
            children,
            infants,
            qty,
            isIncluded: templateItem.isIncluded,
            isOptional: templateItem.isOptional,
            customPricing: templateItem.customPricing,
            remarks: templateItem.remarks,
            ratePlan: templateItem.ratePlan,
            pricing: templateItem.customPricing
              ? {
                  adultBuy: templateItem.adultBuy,
                  adultSell: templateItem.adultSell,
                  childBuy: templateItem.childBuy,
                  childSell: templateItem.childSell,
                  infantBuy: templateItem.infantBuy,
                  infantSell: templateItem.infantSell,
                  itemBuy: templateItem.itemBuy,
                  itemSell: templateItem.itemSell,
                  currencyBuy: templateItem.currencyBuy,
                  currencySell: templateItem.currencySell,
                  rateTypeCode: templateItem.rateTypeCode,
                }
              : undefined,
            extras: templateItem.extras.length
              ? templateItem.extras.map((e) => {
                  const extraDay = Number(e.day) - 1;
                  const extraStartDate = moment(this.pricingDate).add(
                    extraDay,
                    "days"
                  );
                  const extraDuration =
                    templateItem.serviceType == "ACCOMMODATION"
                      ? Number(e.duration) + 1
                      : Number(e.duration);
                  const extraEndDate = moment(extraStartDate).add(
                    extraDuration,
                    "days"
                  );

                  const pricing = e.customPricing
                    ? {
                        cost: e.cost,
                        sell: e.sell,
                        currency: e.currencyBuy,
                        currencyExchangeRate: e.currencyBuyExchangeRate,
                        currencySellExchangeRate: e.currencySellExchangeRate,
                        rateTypeCode: e.rateTypeCode,
                      }
                    : undefined;

                  return {
                    id: e.extraId ? e.extraId : e.id,
                    uid: e.extraId ? e.extraId : e.uid,
                    //qty: e.qty,
                    qty: noOfPax,
                    startDate: e.startDate ? e.startDate : extraStartDate,
                    endDate: e.endDate ? e.endDate : extraEndDate,
                    pricing,
                  };
                })
              : undefined,
          };
        }),
      };
      return input;
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    title() {
      return this.increment
        ? `${this.$options.filters.formatDate(
            this.increment.date
          )} - Min Pax: ${this.increment.minPax}, Max Pax: ${
            this.increment.maxPax
          }`
        : "Increment Title";
    },
    items() {
      if (this.increment) {
        const list = [];
        let n = Number(this.minPax);
        while (n <= Number(this.maxPax)) {
          if (n + Number(this.step) - 1 <= Number(this.maxPax)) {
            list.push({
              uid: `${n}-${n + Number(this.step)}`,
              paxFrom: n,
              paxTo: n + Number(this.step) - 1,
              adjustment: this.adjustmentsForPax(n, n + Number(this.step) - 1),
            });
          }
          n += Number(this.step);
        }

        return list;
      } else {
        return [];
      }
    },
  },

  watch: {
    increment(newVal) {
      //console.log(newVal);
      this.minPax = newVal.minPax;
      this.maxPax = newVal.maxPax;
      this.step = newVal.step;
      this.adjustments =
        newVal.adjustments && newVal.adjustments.edges
          ? newVal.adjustments.edges.map(removeGQLTypename)
          : [];
    },
  },

  methods: {
    updateAdjustments(f, payload, index) {
      //console.log("updating", f, payload, index);
      this.adjustments = [
        ...this.adjustments.map((a) => {
          return {
            ...a,
            [`${f}${index}`]: Number(payload),
            // [`${f}2`]: Number(payload),
            // [`${f}3`]: Number(payload),
            // [`${f}4`]: Number(payload),
          };
        }),
      ];
      //check for new pax conf adjustment
      for (const item of this.items) {
        //console.log("checking adjustment for item:", item);
        if (
          !this.adjustments.find(
            (a) => a.paxFrom == item.paxFrom && a.paxTo == item.paxTo
          )
        ) {
          this.adjustments.push({
            ...this.getBlankAdjustment(item.paxFrom, item.paxTo),
            [`${f}${index}`]: Number(payload),
            // [`${f}2`]: Number(payload),
            // [`${f}3`]: Number(payload),
            // [`${f}4`]: Number(payload),
          });
        }
      }
    },
    getBlankAdjustment(paxFrom, paxTo) {
      return {
        id: randomize("Aa0", 32),
        paxFrom,
        paxTo,
        adjustment1: null,
        adjustment2: null,
        adjustment3: null,
        adjustment4: null,
        adjustment100: null,
        adjustment200: null,
        foc1: null,
        foc2: null,
        foc3: null,
        foc4: null,
        foc100: null,
        foc200: null,
        childAdjustment1: null,
        childAdjustment2: null,
        childAdjustment3: null,
        childAdjustment4: null,
        childAdjustment100: null,
        childAdjustment200: null,
        childFoc1: null,
        childFoc2: null,
        childFoc3: null,
        childFoc4: null,
        childFoc100: null,
        childFoc200: null,
        infantAdjustment1: null,
        infantAdjustment2: null,
        infantAdjustment3: null,
        infantAdjustment4: null,
        infantAdjustment100: null,
        infantAdjustment200: null,
        infantFoc1: null,
        infantFoc2: null,
        infantFoc3: null,
        infantFoc4: null,
        infantFoc100: null,
        infantFoc200: null,
      };
    },
    getPriceByPaxType(paxType, item) {
      let pricingItem = null;
      if (paxType == "adults") {
        pricingItem = this.itinerary.items.edges.find(
          (i) => i.node.id == item.id
        );
      } else if (paxType == "children") {
        pricingItem = this.itineraryForChildren.items.edges.find(
          (i) => i.node.id == item.id
        );
      } else if (paxType == "infants") {
        pricingItem = this.itineraryForInfants.items.edges.find(
          (i) => i.node.id == item.id
        );
      }
      //console.log(paxType, pricingItem);
      if (pricingItem) {
        let price;
        if (pricingItem.node.serviceTypeName == "ACCOMMODATION") {
          price =
            Number(pricingItem.node.totalPrice) /
            Number(pricingItem.node.pax[paxType]);
        } else if (!pricingItem.node.pricing.itemBased) {
          const paxCount = Number(pricingItem.node.pax[paxType]);
          price =
            paxCount > 0
              ? Number(pricingItem.node.totalPrice) / paxCount
              : 0;
        } else {
          price = Number(pricingItem.node.totalPrice);
        }
        //console.log(paxType, price, pricingItem.node.pricing.itemBased);
        //infant value for per unit services should always be 0. - https://tracker.globetrack.ie/issues/8567
        if (paxType == "infants" && pricingItem.node.pricing.itemBased) {
          price = 0;
        }
        //add total for extras
        if (item.extras.length > 0) {
          //only per person extras
          //const extras = item.extras.filter((e) => e.rateBase == "P");
          //8605 all extras now
          const extrasForPaxType = item.extras.filter((e) => {
            if (e.isChildOnly) {
              return paxType == "children";
            } else if (e.isInfantOnly) {
              return paxType == "infants";
            } else {
              return paxType == "adults";
            }
          });
          //console.log(paxType, price, extrasForPaxType);
          const totalExtras = extrasForPaxType.reduce((sum, extra) => {
            return sum + this.getPriceForExtra(extra);
          }, 0);
          //console.log(item, totalExtras);
          price += totalExtras;
        }

        return price;
      } else {
        return 0;
      }
    },
    getPriceForExtra(extra) {
      let divider = 1;
      if (extra.rateBase == "I") {
        divider = extra.qty;
      } else {
        if (extra.isChildOnly) {
          divider = extra.children;
        } else if (extra.isInfantOnly) {
          divider = extra.infants;
        } else {
          divider = extra.adults;
        }
      }
      return extra.totalSell / divider;
    },
    onShowCreateItinerary(payload) {
      if (this.forItinerary) {
        this.$emit("updateItinerary", payload);
      } else {
        this.itiCharges = [...payload.charges];
        this.showCreateItineraryFromTemplate = true;
      }
    },
    onCreateItinerary(payload) {
      const charges = this.itiCharges.filter(
        (ch) =>
          payload.roomTypes
            .map((rt) => rt.occupancy)
            .indexOf(Number(ch.occupancy)) > -1
      );
      const recalcInput = {
        ...payload.itineraryInput,
        items: [...payload.itineraryInput.items],
      };
      //recalc to get prices
      this.$apollo
        .mutate({
          mutation: ITINERARY_RECALCULATE_MUTATION,
          variables: recalcInput,
          fetchPolicy: "no-cache",
        })
        .then(({ data }) => {
          const itiInput = {
            ...recalcInput,
            items: [
              ...data.recalculateItinerary.items.edges
                .map(removeGQLTypename)
                .filter((item) => item.serviceTypeName != "MANDATORY_CHARGES")
                .map(this.fromItiItemToInput)
                .map(this.setZeroPrices),
              ...charges.map(
                this.chargeToItiInput(payload.roomTypes, recalcInput)
              ),
            ],
          };
          this.createItinerary(itiInput);
        })
        .catch((err) => console.error(err));
    },
    setZeroPrices(item) {
      return {
        ...item,
        pricing: {
          adultBuy: item.pricing.adultBuy,
          adultSell: 0,
          childBuy: item.pricing.childBuy,
          childSell: 0,
          infantBuy: item.pricing.infantBuy,
          infantSell: 0,
          itemBuy: item.pricing.itemBuy,
          itemSell: 0,
          rateTypeCode: item.pricing.rateTypeCode,
          currencySell: item.pricing.currencySell,
          currencyBuy: item.pricing.currencyBuy,
        },
      };
    },

    chargeToItiInput(roomTypes, itiInput) {
      return (item) => {
        const roomSettings = roomTypes.find(
          (r) => Number(r.occupancy) == Number(item.occupancy)
        );
        const date = itiInput.endDate; // this.pricingDate;
        return {
          id: randomize("Aa0", 32),
          adults: roomSettings ? Number(roomSettings.adults) : 0,
          children: roomSettings ? Number(roomSettings.children) : 0,
          infants: roomSettings ? Number(roomSettings.infants) : 0,
          //qty: roomSettings ? Number(roomSettings.qty) : 0,
          //7894
          // "The Qty should always be 1 for the "Charges" lines as the rate type is PP"  - Christina Gore
          qty: roomSettings ? 1 : 0,
          startDate: date,
          endDate: date,
          serviceName: item.name,
          serviceType: "MANDATORY_CHARGES",
          serviceId: "",
          isIncluded: true,
          isOptional: false,
          pricing: {
            adultBuy: 0,
            adultSell: item.total,
            childBuy: 0,
            childSell: 0,
            infantBuy: 0,
            infantSell: 0,
            itemBuy: 0,
            itemSell: 0,
            rateTypeCode: "PP",
            currencySell: this.$store.getters.itineraryAgentCurrency,
            currencyBuy: this.$store.getters.itineraryAgentCurrency,
          },
        };
      };
    },
    fromItiItemToInput(item) {
      return {
        id: item.id,
        serviceId: item.service ? item.service.id : "",
        adults: item.pax.adults,
        children: item.pax.children,
        infants: item.pax.infants,
        qty: item.qty,
        startDate: item.startDate,
        endDate: item.endDate,
        serviceType: item.serviceTypeName,
        pricing: { ...item.pricing },
      };
    },
    adjustmentsForPax(paxFrom, paxTo) {
      const adjustment = this.adjustments.find(
        (a) => a.paxFrom == paxFrom && a.paxTo == paxTo
      );
      if (adjustment) {
        const { id, paxFrom, paxTo, ...adj } = adjustment;
        return { ...adj };
      } else {
        return null;
      }
    },
    onDelete() {
      this.$emit("delete", { ...this.increment });
    },
    onPaxPricingChange(payload) {
      const editedIndex = this.adjustments.findIndex(
        (a) => a.paxFrom == payload.paxFrom && a.paxTo == payload.paxTo
      );
      // console.log("onPaxPricingChange", payload);
      // console.log("editedIndex", editedIndex);
      if (editedIndex > -1) {
        Object.assign(this.adjustments[editedIndex], { ...payload });
      } else {
        this.adjustments.push(payload);
      }
      //console.log("adjustments", this.adjustments);
      this.emitChange();
    },
    emitChange() {
      this.$emit("change", {
        id: this.increment.id,
        step: Number(this.step),
        minPax: Number(this.minPax),
        maxPax: Number(this.maxPax),
        adjustments: this.adjustments,
      });
    },
    onSave() {
      this.$emit("update", {
        ...this.increment,
        step: Number(this.step),
        minPax: Number(this.minPax),
        maxPax: Number(this.maxPax),
        adjustments: this.adjustments,
      });
    },
    createItinerary(payload) {
      this.$apollo
        .mutate({
          mutation: CREATE_ITINERARY,
          variables: { input: payload },
        })
        .then(({ data }) => {
          this.showCreateItineraryFromTemplate = false;
          this.$store.dispatch("showAppSnackbar", {
            msg: "Itinerary Created.",
          });
          this.$router.push(`/itinerary/${data.createItinerary.id}/details`);
        })
        .catch((err) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: err,
            color: "error",
          });
          console.error(err);
        });
    },
    async recalculate() {
      try {
        this.loading = true;
        const res = await this.$apollo.mutate({
          mutation: ITINERARY_RECALCULATE_MUTATION,
          variables: this.itineraryInput,
          fetchPolicy: "no-cache",
        });
        //console.log("recalc response", res.data.recalculateItinerary);
        this.itinerary = { ...res.data.recalculateItinerary };

        //then children
        const resForChildren = await this.$apollo.mutate({
          mutation: ITINERARY_RECALCULATE_MUTATION,
          variables: this.childrenItiInput,
          fetchPolicy: "no-cache",
        });
        this.itineraryForChildren = {
          ...resForChildren.data.recalculateItinerary,
        };

        //and infants
        const resForInfants = await this.$apollo.mutate({
          mutation: ITINERARY_RECALCULATE_MUTATION,
          variables: this.infantsItiInput,
          fetchPolicy: "no-cache",
        });

        this.itineraryForInfants = {
          ...resForInfants.data.recalculateItinerary,
        };

        this.loading = false;
      } catch (error) {
        console.error(error);
        this.loading = false;
      }
    },
    onVisibleColumnsChange(event) {
      this.visibleColumns = [...event];
      this.$store.dispatch("updateLocalSettings", {
        incrementsColumns: event,
      });
    },
  },

  mounted() {
    this.recalculate();
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/templates/builder/increments/IncrementCombined.vue